import { createContext, useContext, useState, ReactNode } from "react";
import { useEffect } from "react";
import Swal from "sweetalert2";
import { useModal } from "../../../hooks/useModal";
import {
  useLojas,
  useCreateLoja,
  useUpdateLoja,
  useDeleteLoja,
} from "../../../hooks/useLojas";
import { useGlobalStore } from "../../../store/globalStore";
import { LojaFormData } from "../../../lib/validations";
import { toast } from "react-toastify";
import { COLORS, MESSAGES } from "../../../constants";

interface FilterState {
  nome: string;
  endereco: string;
  telefone: string;
  cnpj: string;
}

interface LojaContextType {
  filters: FilterState;
  searchFilters: FilterState;
  handleFieldChange: (name: string, value: string) => void;
  handleSearch: () => void;
  handleClear: () => void;

  isOpen: boolean;
  editingItem: any;
  openModal: (item?: any) => void;
  closeModal: () => void;

  lojas: any[];
  isLoading: boolean;
  handleSubmit: (formData: LojaFormData) => Promise<void>;
  handleEdit: (rowIndex: number) => void;
  handleDelete: (rowIndex: number) => Promise<void>;
}

const LojaContext = createContext<LojaContextType | undefined>(undefined);

export const LojaProvider = ({ children }: { children: ReactNode }) => {
  const [filters, setFilters] = useState<FilterState>({
    nome: "",
    endereco: "",
    telefone: "",
    cnpj: "",
  });

  const [searchFilters, setSearchFilters] = useState<FilterState>({
    nome: "",
    endereco: "",
    telefone: "",
    cnpj: "",
  });

  const { isOpen, editingItem, openModal, closeModal } = useModal();
  const { error, clearError } = useGlobalStore();

  const { data: lojas = [] } = useLojas(searchFilters);
  const createMutation = useCreateLoja();
  const updateMutation = useUpdateLoja();
  const deleteMutation = useDeleteLoja();

  useEffect(() => {
    const allErrors = [
      createMutation.error,
      updateMutation.error,
      deleteMutation.error,
    ].filter(Boolean);

    if (allErrors.length > 0) {
      Swal.fire("Erro Identificado", allErrors[0]?.message, "error");
    }
  }, [createMutation.error, updateMutation.error, deleteMutation.error]);

  useEffect(() => {
    if (error) {
      Swal.fire("Erro Identificado", error, "error");
      clearError();
    }
  }, [error, clearError]);

  const handleFieldChange = (name: string, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    setSearchFilters(filters);
  };

  const handleClear = () => {
    setFilters({ nome: "", endereco: "", telefone: "", cnpj: "" });
    setSearchFilters({ nome: "", endereco: "", telefone: "", cnpj: "" });
  };

  const handleSubmit = async (formData: LojaFormData) => {
    try {
      if (editingItem) {
        await updateMutation.mutateAsync({
          id: editingItem.id,
          data: formData,
        });
        toast.success(MESSAGES.UPDATE);
      } else {
        await createMutation.mutateAsync(formData);
        toast.success(MESSAGES.CREATE);
      }
      closeModal();
    } catch (error) {
      console.error("Erro ao salvar loja:", error);
    }
  };

  const handleEdit = (rowIndex: number) => {
    openModal(lojas[rowIndex]);
  };

  const handleDelete = async (rowIndex: number) => {
    const result = await Swal.fire({
      title: "Tem certeza?",
      text: "Esta ação não pode ser desfeita",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: COLORS.PRIMARY,
      cancelButtonColor: COLORS.NEUTRAL_500,
      confirmButtonText: "Sim, deletar!",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        await deleteMutation.mutateAsync(lojas[rowIndex].id);
        Swal.fire({
          title: "Deletado!",
          text: "A loja foi removida com sucesso.",
          icon: "success",
          confirmButtonColor: COLORS.PRIMARY,
        });
      } catch (error) {
        console.error("Erro ao deletar loja:", error);
      }
    }
  };

  return (
    <LojaContext.Provider
      value={{
        filters,
        searchFilters,
        handleFieldChange,
        handleSearch,
        handleClear,
        isOpen,
        editingItem,
        openModal,
        closeModal,
        lojas,
        isLoading: createMutation.isPending || updateMutation.isPending,
        handleSubmit,
        handleEdit,
        handleDelete,
      }}
    >
      {children}
    </LojaContext.Provider>
  );
};

export const useLoja = () => {
  const context = useContext(LojaContext);
  if (!context) {
    throw new Error("useLoja must be used within LojaProvider");
  }
  return context;
};
